import { useQuery } from '@tanstack/react-query';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { type CarFuel, FuelBars } from '../../components/ui/FuelBars.js';
import { LocationMap } from '../../components/ui/LocationMap.js';
import { Spinner } from '../../components/ui/Spinner.js';
import { ZoneRouteMap } from '../../components/ui/ZoneRouteMap.js';
import { CARTRACK_KEYS, cartrackApi, refusal } from '../../services/cartrack.js';
import { mytClock, mytDayLabel } from '../../utils/myt.js';

/**
 * 405 (#315, mock-up C1–C3): where the car is now, from Cartrack. The platform decides who may read it (an
 * administrator, or a host assigned to the car); for anyone else it answers 404 and this card shows nothing. A car
 * not on Cartrack, or Cartrack not answering, keeps the pasted GPS tracker link as before.
 */
function safeHttpUrl(value: string | undefined): string | null {
  if (!value) return null;
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : null;
  } catch {
    return null;
  }
}

const when = (iso: string | null) => (iso ? `${mytDayLabel(iso)}, ${mytClock(iso)}` : 'unknown');

export function CarPositionCard({
  carId,
  trackingUrl,
  fuel,
}: {
  carId: string;
  trackingUrl?: string;
  /** 408 (#1434): the car's fuel from `GET /cars/:id` (staff: with litres); absent when there is none. */
  fuel?: CarFuel;
}) {
  const position = useQuery({
    queryKey: CARTRACK_KEYS.position(carId),
    queryFn: () => cartrackApi.position(carId),
  });
  if (refusal(position.error)?.status === 404) return null;

  const link = safeHttpUrl(trackingUrl);
  const pasted = link ? (
    <>
      {' '}
      GPS tracker link:{' '}
      <a href={link} target="_blank" rel="noopener noreferrer" className="text-primary">
        open
      </a>
    </>
  ) : null;
  const p = position.data;

  return (
    <Card>
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-ink">Live position</h2>
        {p?.state === 'live' && (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            loading={position.isFetching}
            onClick={() => position.refetch()}
          >
            Refresh
          </Button>
        )}
      </div>
      {position.isPending && <Spinner />}
      {position.isError && (
        <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
          Could not load the position. The rest of this page works as usual.
        </p>
      )}
      {p?.state === 'live' && (
        <>
          {/* 416 (#1431, mock-up screen 9): with a location zone, the outline around the dot (orange outside). */}
          {p.zone ? (
            <>
              <ZoneRouteMap
                testId="car-zone-map"
                polygon={p.zone.polygon}
                dot={{ lng: p.longitude, lat: p.latitude, inside: p.zone.inside }}
              />
              {p.zone.inside !== null && (
                <p
                  data-testid="car-zone-status"
                  className={`mt-2 inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${
                    p.zone.inside
                      ? 'bg-semantic-success-bg text-semantic-success-text'
                      : 'bg-semantic-warning-bg text-semantic-warning-text'
                  }`}
                >
                  {p.zone.inside ? 'Inside its location zone' : 'Outside its location zone'}
                </p>
              )}
            </>
          ) : (
            // `LocationMap` contains Leaflet's z-index itself (#1427), so it never covers the sticky header.
            <LocationMap
              value={{ latitude: p.latitude, longitude: p.longitude }}
              interactive={false}
            />
          )}
          {p.stale ? (
            <p
              data-testid="position-stale"
              className="mt-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800"
            >
              Last seen {when(p.fix_at)}
              {p.ignition === false ? ' · ignition off' : ''}
            </p>
          ) : (
            <dl
              data-testid="position-facts"
              className="mt-3 grid grid-cols-[120px_1fr] gap-x-4 gap-y-1 text-sm"
            >
              <dt className="text-steel">Updated</dt>
              <dd>{when(p.fix_at)}</dd>
              <dt className="text-steel">Ignition</dt>
              <dd>
                {p.ignition === null ? 'Unknown' : p.ignition ? 'On' : 'Off'}
                {p.ignition && p.speed_kmh !== null ? ` · ${p.speed_kmh} km/h` : ''}
              </dd>
              <dt className="text-steel">Odometer</dt>
              <dd>
                {p.odometer_km === null ? 'Unknown' : `${p.odometer_km.toLocaleString('en-MY')} km`}
              </dd>
            </dl>
          )}
        </>
      )}
      {p?.state === 'unlinked' && (
        <p data-testid="position-unlinked" className="text-sm text-steel">
          This car is not on Cartrack.{pasted}
        </p>
      )}
      {p?.state === 'unavailable' && (
        <>
          <p
            data-testid="position-unavailable"
            className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700"
          >
            Cartrack could not be reached. The rest of this page works as usual.
          </p>
          {pasted && <p className="mt-2 text-sm text-steel">{pasted}</p>}
        </>
      )}
      {/* 408 (mock-up screen 4): the fuel, from the same Cartrack reading. */}
      {fuel && (
        <dl className="mt-3 grid grid-cols-[120px_1fr] gap-x-4 text-sm">
          <dt className="text-steel">Fuel</dt>
          <dd>
            <FuelBars fuel={fuel} />
          </dd>
        </dl>
      )}
    </Card>
  );
}
